/**
 * URL utilities for language-prefixed routing
 */

const LOCAL_STORAGE_KEY = 'gantty_language';

/**
 * Supported language codes (matching plugin)
 */
const SUPPORTED_LANGUAGES = [
  'en',
  'hu',
  'ar',
  'fr',
  'zh-cn',
  'ko',
  'it',
  'es',
  'ja',
  'ru',
  'tr',
  'pt-br',
] as const;

/**
 * Extract language code from URL path
 * @param path - URL path (e.g., '/en-us/profile' or '/hu')
 * @returns Language code if found, null otherwise
 */
export function getLanguageFromUrl(path: string = window.location.pathname): string | null {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const firstSegment = segments[0].toLowerCase();
  
  // Check if first segment is a supported language
  if (SUPPORTED_LANGUAGES.includes(firstSegment as any)) {
    return firstSegment;
  }

  return null;
}

/**
 * Get path without language prefix
 * @param path - URL path (e.g., '/en-us/profile')
 * @returns Path without language prefix (e.g., '/profile')
 */
export function getPathWithoutLanguage(path: string = window.location.pathname): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return '/';

  const firstSegment = segments[0].toLowerCase();
  
  // If first segment is a language code, remove it
  if (SUPPORTED_LANGUAGES.includes(firstSegment as any)) {
    const remainingPath = '/' + segments.slice(1).join('/');
    return remainingPath === '/' ? '/' : remainingPath;
  }

  return path;
}

/**
 * Add language prefix to path
 * @param lang - Language code (e.g., 'en-us', 'hu')
 * @param path - Path to add language to (e.g., '/profile')
 * @returns Path with language prefix (e.g., '/en-us/profile')
 */
export function addLanguageToPath(lang: string, path: string): string {
  // Normalize path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove any existing language prefix
  const pathWithoutLang = getPathWithoutLanguage(normalizedPath);
  
  // Add language prefix
  return `/${lang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
}

/**
 * Detect language from various sources
 * Priority: URL → localStorage → browser → default to 'en'
 */
export function detectLanguage(): string {
  // 1. Check URL
  const urlLang = getLanguageFromUrl();
  if (urlLang) {
    return urlLang;
  }

  // 2. Check localStorage
  const storedLang = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (storedLang) {
    // Normalize stored language (convert to lowercase with dash)
    const normalized = storedLang.toLowerCase().replace('_', '-');
    if (SUPPORTED_LANGUAGES.includes(normalized as any)) {
      return normalized;
    }
  }

  // 3. Check browser language
  const browserLang = navigator.language.toLowerCase();
  const browserLangBase = browserLang.split('-')[0];
  
  // Handle Spanish variants (map to es)
  if (browserLang.startsWith('es-')) {
    return 'es';
  }

  // Try full browser language first (e.g., 'en-us')
  const browserLangNormalized = browserLang.replace('_', '-');
  if (SUPPORTED_LANGUAGES.includes(browserLangNormalized as any)) {
    return browserLangNormalized;
  }
  
  // Try base language (e.g., 'en' from 'en-US')
  if (SUPPORTED_LANGUAGES.includes(browserLangBase as any)) {
    return browserLangBase;
  }

  // 4. Default to English
  return 'en';
}

/**
 * Redirect to path with language prefix preserved or detected
 * @param path - Path to redirect to (e.g., '/profile', '/')
 */
export function redirectWithLanguage(path: string): void {
  const currentLang = getLanguageFromUrl() || detectLanguage();
  const newPath = addLanguageToPath(currentLang, path);
  window.location.href = newPath;
}

/**
 * Get current language from URL or detect it
 */
export function getCurrentLanguage(): string {
  return getLanguageFromUrl() || detectLanguage();
}

